/**
 * The robo-cat tutor head, ported from docs/ref/robocat-demo.html: ears, screen with LED
 * eyes and scanlines, white mustache plate, side LEDs. The parent chooses the state; an
 * `alive` cat in the idle state also has moods of its own (src/core/cat-mood.ts): it
 * startles when touched, dozes off when nothing happens, wakes on a tap and gets angry when
 * poked over and over.
 */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useCatMood } from './use-cat-mood.ts';
import './robocat.css';

export type RoboCatState = 'idle' | 'listening' | 'thinking' | 'happy' | 'sad' | 'surprised' | 'angry';

/** What is actually drawn: the parent's state, or the cat's own sleepy moods. */
type Look = RoboCatState | 'sleepy' | 'asleep';

export const DEFAULT_EYE_COLOR = '#34d399';

/** Every cat is drawn this much larger than the size its caller asks for. */
const SCALE = 1.2;

export const EYE_COLORS: readonly { value: string; label: string }[] = [
  { value: '#34d399', label: 'Зелёный' },
  { value: '#2dd4bf', label: 'Бирюзовый' },
  { value: '#60a5fa', label: 'Голубой' },
  { value: '#fbbf24', label: 'Янтарный' },
  { value: '#f472b6', label: 'Розовый' },
  { value: '#f1f5f9', label: 'Белый' },
];

/** The learner's chosen LED colour (Профиль → цвет глаз). */
export const RoboCatColorContext = createContext<string>(DEFAULT_EYE_COLOR);

const STATE_LED: Partial<Record<Look, string>> = {
  thinking: '#facc15',
  happy: '#2dd4bf',
  angry: '#f87171',
  sad: '#7cc4ff',
};

interface EyeShape {
  x: [number, number];
  y: number;
  width: number;
  height: number;
  rx: number;
}

const BASE_EYES: EyeShape = { x: [72, 114], y: 84, width: 14, height: 22, rx: 6 };
const STATE_EYES: Partial<Record<Look, EyeShape>> = {
  sleepy: { ...BASE_EYES, y: 96, height: 6, rx: 3 },
  asleep: { ...BASE_EYES, y: 99, height: 3, rx: 1.5 },
  sad: { ...BASE_EYES, y: 94, height: 12 },
  angry: { ...BASE_EYES, y: 90, height: 14, rx: 3 },
  surprised: { x: [71, 113], y: 80, width: 16, height: 28, rx: 14 },
};
const BLINK = { y: 93, height: 4 };

const SPARKS = 8;
const BLINK_MS = 130;

interface Gaze {
  x: number;
  y: number;
}

function stateGaze(state: RoboCatState, typedLength: number): Gaze | null {
  if (state === 'thinking') return { x: 0.7, y: -0.8 };
  if (state === 'listening') return { x: Math.sin(typedLength * 0.45) * 0.8, y: 0.6 };
  return null;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export interface RoboCatProps {
  state?: RoboCatState;
  size?: number;
  /** Length of the text being typed; drives the eyes in the `listening` state. */
  typedLength?: number;
  /** Eyes follow the pointer/finger while idle. */
  followPointer?: boolean;
  /** Accessible name; omit for a decorative cat. */
  label?: string;
  /** Reacts to touch, falls asleep when idle, gets angry when poked (idle state only). */
  alive?: boolean;
  className?: string;
}

export function RoboCat({
  state = 'idle',
  size = 46,
  typedLength = 0,
  followPointer = false,
  label,
  className,
  alive = false,
}: RoboCatProps) {
  const eyeColor = useContext(RoboCatColorContext);
  const reducedMotion = usePrefersReducedMotion();
  const ids = useId().replace(/:/g, '');
  const stageRef = useRef<HTMLDivElement>(null);
  const [pointerGaze, setPointerGaze] = useState<Gaze>({ x: 0, y: 0 });
  const [blinking, setBlinking] = useState(false);
  const [burst, setBurst] = useState(0);
  const { phase, poke } = useCatMood(alive && state === 'idle');
  const look: Look =
    state !== 'idle'
      ? state
      : phase === 'angry'
        ? 'angry'
        : phase === 'yawning'
          ? 'sleepy'
          : phase === 'asleep'
            ? 'asleep'
            : 'idle';
  const sleeping = look === 'sleepy' || look === 'asleep';

  // Random blink every 2.6–5 s (not while happy: the eyes are arcs then).
  useEffect(() => {
    if (state === 'happy' || sleeping) return;
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(() => {
        setBlinking(true);
        timer = window.setTimeout(() => {
          setBlinking(false);
          schedule();
        }, BLINK_MS);
      }, 2600 + Math.random() * 2400);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [state, sleeping]);

  // Follow the pointer while idle.
  useEffect(() => {
    if (!followPointer || look !== 'idle') return;
    let frame = 0;
    const onPointer = (e: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = stageRef.current?.getBoundingClientRect();
        if (!rect) return;
        let x = (e.clientX - (rect.left + rect.width / 2)) / (rect.width * 0.9);
        let y = (e.clientY - (rect.top + rect.height * 0.45)) / (rect.height * 0.9);
        const length = Math.hypot(x, y);
        if (length > 1) {
          x /= length;
          y /= length;
        }
        setPointerGaze({ x, y });
      });
    };
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerdown', onPointer, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [followPointer, look]);

  // One bounce + sparkle burst each time the cat becomes happy.
  useEffect(() => {
    if (state !== 'happy' || reducedMotion) return;
    setBurst((n) => n + 1);
    const timer = window.setTimeout(() => setBurst(0), 1300);
    return () => window.clearTimeout(timer);
  }, [state, reducedMotion]);

  const led = STATE_LED[look] ?? eyeColor;
  const gaze = stateGaze(state, typedLength) ?? (look === 'idle' && followPointer ? pointerGaze : { x: 0, y: 0 });
  const eyes = STATE_EYES[look] ?? BASE_EYES;
  const startled = phase === 'startled' && state === 'idle';
  const blinkNow = (blinking || startled) && !reducedMotion && look !== 'surprised' && !sleeping;
  const showBrows = look === 'sad' || look === 'angry';
  const browL = look === 'sad' ? 'rotate(-18 78 78)' : 'rotate(20 78 80)';
  const browR = look === 'sad' ? 'rotate(18 122 78)' : 'rotate(-20 122 80)';
  const px = Math.round(size * SCALE);
  const fury = alive && state === 'idle' && phase === 'angry';

  const classes = [
    'robocat',
    `robocat--${look}`,
    burst > 0 ? 'robocat--bounce' : '',
    alive ? 'robocat--alive' : '',
    startled ? 'robocat--startle' : '',
    phase === 'waking' && state === 'idle' ? 'robocat--wake' : '',
    phase === 'yawning' && state === 'idle' ? 'robocat--yawn' : '',
    fury ? 'robocat--fury' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const style = { width: px, height: px, '--rc': `${px}px` } as CSSProperties;

  return (
    <div
      className={classes}
      style={style}
      ref={stageRef}
      onPointerDown={alive && state === 'idle' ? poke : undefined}
    >
      <svg
        viewBox="0 0 200 200"
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        focusable="false"
      >
        <g className="robocat__breath">
          <g className="robocat__char">
            <ellipse cx="100" cy="168" rx="44" ry="6" fill="#000" opacity=".08" />
            <path d="M44 70 L47 34 L78 58 Z" fill="#7A7F8C" stroke="#4A4F5A" strokeWidth="3" strokeLinejoin="round" />
            <path d="M51 64 L53 44 L69 58 Z" fill="#E7A9B4" />
            <path d="M156 70 L153 34 L122 58 Z" fill="#7A7F8C" stroke="#4A4F5A" strokeWidth="3" strokeLinejoin="round" />
            <path d="M149 64 L147 44 L131 58 Z" fill="#E7A9B4" />
            <rect x="33" y="92" width="6" height="26" rx="2" fill={led} />
            <rect x="161" y="92" width="6" height="26" rx="2" fill={led} />
            <rect x="38" y="54" width="124" height="98" rx="26" fill="#7A7F8C" stroke="#4A4F5A" strokeWidth="3" />
            {alive && <rect className="robocat__flush" x="38" y="54" width="124" height="98" rx="26" fill="#EF4444" />}
            <rect x="50" y="66" width="100" height="62" rx="16" fill="#0B0B0F" />
            <g clipPath={`url(#scr-${ids})`}>
              <g
                className="robocat__eyes"
                style={{ transform: `translate(${gaze.x * 10}px, ${gaze.y * 6}px)` }}
              >
                {look === 'happy' ? (
                  <>
                    <path d="M71 100 q8 -12 16 0" stroke={led} strokeWidth="6" strokeLinecap="round" fill="none" />
                    <path d="M113 100 q8 -12 16 0" stroke={led} strokeWidth="6" strokeLinecap="round" fill="none" />
                  </>
                ) : (
                  eyes.x.map((x) => (
                    <rect
                      key={x}
                      x={x}
                      y={blinkNow ? BLINK.y : eyes.y}
                      width={eyes.width}
                      height={blinkNow ? BLINK.height : eyes.height}
                      rx={eyes.rx}
                      fill={led}
                    />
                  ))
                )}
              </g>
              {showBrows && (
                <g>
                  <rect x="66" y="76" width="24" height="5" rx="2" fill={led} transform={browL} />
                  <rect x="110" y="76" width="24" height="5" rx="2" fill={led} transform={browR} />
                </g>
              )}
              <rect x="50" y="66" width="100" height="62" fill={`url(#scan-${ids})`} />
            </g>
            {look === 'sad' && (
              <g>
                <path className="robocat__tear" d="M79 112 q-5 8 0 11 q5 -3 0 -11z" fill="#7cc4ff" />
                <path className="robocat__tear robocat__tear--late" d="M121 112 q-5 8 0 11 q5 -3 0 -11z" fill="#7cc4ff" />
              </g>
            )}
            {look === 'angry' && (
              <g className="robocat__steam">
                <path d="M30 40 q-6 -8 0 -14 q6 -6 0 -14" stroke="#c9c2bb" strokeWidth="3" fill="none" strokeLinecap="round" />
                <path d="M170 40 q6 -8 0 -14 q-6 -6 0 -14" stroke="#c9c2bb" strokeWidth="3" fill="none" strokeLinecap="round" />
              </g>
            )}
            <path
              d="M100 132 q-8 -2 -16 4 q-6 6 2 8 q8 1 14 -5 q6 6 14 5 q8 -2 2 -8 q-8 -6 -16 -4z"
              fill="#FBF8F3"
            />
            <path d="M96 131 h8 l-4 4z" fill="#C98793" />
            <path
              d="M78 137 L52 132 M78 141 L52 143 M122 137 L148 132 M122 141 L148 143"
              stroke="#FBF8F3"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
        </g>
        <defs>
          <clipPath id={`scr-${ids}`}>
            <rect x="50" y="66" width="100" height="62" rx="16" />
          </clipPath>
          <pattern id={`scan-${ids}`} width="4" height="4" patternUnits="userSpaceOnUse">
            <rect width="4" height="2" fill="#000" opacity=".35" y="2" />
          </pattern>
        </defs>
      </svg>
      {state === 'thinking' && (
        <div className="robocat__dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
      )}
      {look === 'asleep' && (
        <div className="robocat__zzz" aria-hidden="true">
          <i>z</i>
          <i>z</i>
          <i>z</i>
        </div>
      )}
      {burst > 0 &&
        Array.from({ length: SPARKS }, (_, k) => {
          const angle = (k / SPARKS) * Math.PI * 2;
          const sparkStyle = {
            '--dx': `${Math.cos(angle) * px * 0.5}px`,
            '--dy': `${Math.sin(angle) * px * 0.4}px`,
          } as CSSProperties;
          return <span key={`${burst}-${k}`} className="robocat__spark" style={sparkStyle} aria-hidden="true" />;
        })}
    </div>
  );
}
